/*
============================================================
 DataLoader - Factory Pattern for Data Access Layer (DAL)
------------------------------------------------------------
 Abstracts data loading from different sources (files, APIs,
 samples) using a unified interface. Removes data loading 
 logic from UI and provides a clean separation of concerns.

 Usage:
   const loader = new DataLoader(options);
   const puzzleData = await loader.load(sourceType, sourceId);
============================================================
*/

import { FileDataSource } from "./sources/FileDataSource.js";
import { APIDataSource } from "./sources/APIDataSource.js";
import { SampleDataSource } from "./sources/SampleDataSource.js";
import { ArchiveDataSource } from "./sources/ArchiveDataSource.js";

/**
 * Factory-based DataLoader that abstracts data sources
 * Reads URL parameters to determine source and delegates loading
 * @class DataLoader
 */
export class DataLoader {
  /**
   * @param {Object} options - Configuration options
   * @param {string} [options.apiHost] - API host URL (from env)
   * @param {string} [options.dataPath] - Local data folder path
   * @param {string} [options.defaultPuzzle] - Default puzzle filename
   * @param {string} [options.userTimezone] - User's timezone
   */
  constructor(options = {}) {
    this.apiHost = options.apiHost || import.meta.env.VITE_API_HOST_URL || "";
    this.dataPath =
      options.dataPath || import.meta.env.VITE_DATA_PATH || "./data";
    this.samplePath = `${this.dataPath}/sample`;
    this.archivePath = `${this.dataPath}/archive`;
    this.defaultPuzzle = options.defaultPuzzle || "fillitin_default";
    this.userTimezone =
      options.userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Initialize data sources
    this.fileSource = new FileDataSource(this.dataPath);
    this.apiSource = new APIDataSource(this.apiHost);
    this.sampleSource = new SampleDataSource(this.samplePath);
    this.archiveSource = new ArchiveDataSource(this.archivePath);

    console.log("[DataLoader] Initialized:", {
      apiHost: this.apiHost || "(relative path)",
      dataPath: this.dataPath,
      samplePath: this.samplePath,
      archivePath: this.archivePath,
      defaultPuzzle: this.defaultPuzzle,
    });
  }

  /**
   * Determine which loader strategy to use based on identifier
   * Used when sourceType is not explicitly provided
   * @private
   * @param {string} sourceId - The identifier to analyze
   * @returns {string} 'date' | 'file' | 'sample' | 'archive' | 'default'
   */
  _determineSource(sourceId) {
    if (!sourceId) {
      return "default";
    }

    // Check if it's a valid YYYYMMDD date format (date/API source)
    if (this._isValidDateFormat(sourceId)) {
      return "date";
    }

    // Otherwise treat as file identifier
    return "file";
  }

  /**
   * Validate YYYYMMDD date format
   * @private
   * @param {string} dateStr - Date string to validate
   * @returns {boolean}
   */
  _isValidDateFormat(dateStr) {
    if (!dateStr) return false;

    const match = dateStr.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (!match) return false;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    // Validate month and day ranges
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Validate date is actually possible
    const dateObj = new Date(year, month - 1, day);
    return !isNaN(dateObj.getTime());
  }

  /**
   * Main load method - routes to appropriate source
   * Fallback chain depends on source type:
   * - API (date format): API -> Default (skip file, API dates don't exist locally)
   * - File (other format): File -> Default
   * - Sample: Sample -> Default
   * - Archive: Archive -> Default
   * - No source: Load Default
   * @async
   * @param {string} sourceId - The puzzle identifier (YYYYMMDD, filename, sample ID, or archive ID)
   * @param {string} [sourceType] - The source type ('file', 'date', 'sample', 'archive'). If not provided, will be auto-detected.
   * @returns {Promise<Object>} Puzzle data object
   */
  async load(sourceId, sourceType) {
    console.log("[DataLoader] Starting load sequence for:", sourceId || "none");

    const source = sourceType || this._determineSource(sourceId);
    console.log("[DataLoader] Using source type:", source);

    // Date source (YYYYMMDD format): Try API, skip file, go to default
    // Files don't exist for database-backed dates, so checking filesystem wastes time
    if (source === "date" && sourceId) {
      try {
        console.log("[DataLoader] Phase 1: Attempting API load...");
        const data = await this.apiSource.fetch(sourceId, this.userTimezone);
        if (data) {
          console.log("[DataLoader] ✓ Successfully loaded from API");
          return data;
        }
      } catch (error) {
        console.warn("[DataLoader] ✗ API load failed:", error.message);
        console.log(
          "[DataLoader] Skipping file check for date-based puzzle, using default...",
        );
      }
    }

    // File source (filename format): Try file, then default
    if (source === "file" && sourceId) {
      try {
        console.log("[DataLoader] Phase 2: Attempting file load...");
        const data = await this.fileSource.fetch(sourceId);
        if (data) {
          console.log("[DataLoader] ✓ Successfully loaded from file");
          return data;
        }
      } catch (error) {
        console.warn("[DataLoader] ✗ File load failed:", error.message);
      }
    }

    // Sample source (sample puzzle ID): Try sample, then default
    if (source === "sample" && sourceId) {
      try {
        console.log("[DataLoader] Phase 2: Attempting sample load...");
        const data = await this.sampleSource.fetch(sourceId);
        if (data) {
          console.log("[DataLoader] ✓ Successfully loaded from sample");
          return data;
        }
      } catch (error) {
        console.warn("[DataLoader] ✗ Sample load failed:", error.message);
      }
    }

    // Archive source (archive puzzle ID): Try archive, then default
    if (source === "archive" && sourceId) {
      try {
        console.log("[DataLoader] Phase 2: Attempting archive load...");
        const data = await this.archiveSource.fetch(sourceId);
        if (data) {
          console.log("[DataLoader] ✓ Successfully loaded from archive");
          return data;
        }
      } catch (error) {
        console.warn("[DataLoader] ✗ Archive load failed:", error.message);
      }
    }

    // Final fallback to default (reached if no sourceId or all attempts failed)
    try {
      console.log("[DataLoader] Loading default puzzle...");
      const data = await this.fileSource.fetch(this.defaultPuzzle);
      if (data) {
        console.log("[DataLoader] ✓ Successfully loaded default puzzle");
        return data;
      }
    } catch (error) {
      console.error("[DataLoader] ✗ CRITICAL: Default puzzle load failed!");
      throw new Error(`Failed to load any puzzle data: ${error.message}`);
    }

    throw new Error("No puzzle data could be loaded");
  }

  /**
   * Utility: Extract source ID from URL parameters
   * @static
   * @returns {string|null} The source ID (date or filename) or null
   */
  static extractSourceIdFromUrl() {
    const params = new URLSearchParams(window.location.search);

    // Check for explicit file parameter
    if (params.has("file")) {
      return params.get("file");
    }

    // Check for date parameter
    if (params.has("date")) {
      return params.get("date");
    }

    // Check for sample parameter
    if (params.has("sample")) {
      return params.get("sample");
    }

    // Check for archive parameter
    if (params.has("archive")) {
      return params.get("archive");
    }

    // Check legacy 'puzzle' parameter
    if (params.has("puzzle")) {
      return params.get("puzzle");
    }

    return null;
  }

  /**
   * Validate source identifier format
   * @static
   * @param {string} sourceId - Identifier to validate
   * @returns {boolean}
   */
  static isValidSourceId(sourceId) {
    if (!sourceId) return false;
    // Allow alphanumeric, underscore, and dash (for filenames and dates)
    return /^[0-9A-Za-z_-]+$/.test(sourceId.trim());
  }
}
